import { useContext, useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import moment from 'moment'
import { ThemeContext } from 'contexts/theme_context'
import { Button, Table } from 'react-bootstrap'
import { ContentHeader } from 'components/shared/content'
import { SelectField } from 'components/shared/input'
import { InvoiceSearchForm } from 'entities/invoice'
import { useInvoiceApi } from 'api/invoice'
import { InstitutionViewModel } from 'view_models/institution_view_model'
import { downloadFile, selectYears } from 'utils/util'
import { OrganizationViewModel } from 'view_models/organization_view_model'

type Props = {
    history: H.History
}

const InvoiePage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const form = useForm<InvoiceSearchForm>({ year: moment().format('YYYY'), month: moment().format('M') })
    const [aggregate, setAggregate] = useState<{ [key: string]: number | string }>({})
    const api = useInvoiceApi(form)

    useEffect(() => {
        api.execute()
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
    }, [api.loading])

    const createInvoiceCsvList = (hasHeader?: boolean) => {
        const csvList: string[] = []
        if (hasHeader) {
            csvList.push(
                '加盟店取引ID,加盟店取引受注日,企業名,部署名,担当者名,購入企業ID,郵便番号,住所,電話番号１,電話番号2,メールアドレス（To）,メールアドレス（Cc）1,メールアドレス（Cc）2,メールアドレス（Cc）3,メールアドレス（Cc）4,配送先企業名,配送先部署名,配送先担当者名,配送先担当者名（カナ）,配送先郵便番号,配送先住所,配送先電話番号,決済方法,FAX番号,請求書送付方法,コンビニ払い設定,取引金額,明細,元加盟店取引受注日,単価,数量,NP購入企業ID,請求書税区分'
            )
        }

        api.response.organizationInvoices?.forEach((organization) => {
            const TAX_RATE = 0.1
            const vm = new OrganizationViewModel(organization)
            let row = `${form.object.year}${String(form.object.month).padStart(2, '0')}_${organization.code},${moment()
                .set('year', Number(form.object.year))
                .set('M', Number(form.object.month) - 1)
                .endOf('M')
                .format('YYYY/MM/DD')},${organization.name},`
            let totalDiscount = 0
            let totalTax = 0
            let total = 0
            const firstIndex = csvList.length
            const invoices = organization.invoices?.filter((invoice) => invoice.items && invoice.items.length > 0)

            invoices.forEach((invoice, invoiceIndex) => {
                const invoiceItems = invoice.items?.filter((item) => item.price != 0)
                if (invoiceItems?.length === 0) {
                    return
                }
                totalDiscount += invoice.discount ?? 0
                invoiceItems?.forEach((item, index) => {
                    const isFirstRow = invoiceIndex === 0 && index === 0
                    const zipCode = index === 0 ? vm.addressVm().zipCode() : ''
                    const addressWhole = index === 0 ? vm.addressVm().whole() : ''
                    const organizationCode = index === 0 ? organization.code : ''
                    const organizationTel = index === 0 ? organization.tel : ''
                    const totalExceptTax = item.price! - item.discount!
                    const unitPrice = item.price!
                    const productSet = api.response.productSets?.find((ps) => ps.id === item.productSetId)
                    const invoiceDeliveryMethod = isFirstRow ? (invoice.invoiceDeliveryMethod === 'POSTAL' ? '1' : '2') : ''
                    const invoiceSendEmail = isFirstRow && invoice.invoiceSendEmail ? invoice.invoiceSendEmail : ''
                    const invoiceSendEmailCc = isFirstRow && invoice.invoiceSendEmailCc ? invoice.invoiceSendEmailCc : ''
                    const invoiceSendDepartment = isFirstRow && invoice.invoiceSendDepartment ? invoice.invoiceSendDepartment : ''
                    const invoiceSendContactPerson = isFirstRow && invoice.invoiceSendContactPerson ? invoice.invoiceSendContactPerson : ''

                    row =
                        row +
                        `${invoiceSendDepartment},${invoiceSendContactPerson},${organizationCode},${zipCode},${addressWhole},${organizationTel},,${invoiceSendEmail},${invoiceSendEmailCc},,,,,,,,,,,${
                            isFirstRow ? '2' : ''
                        },,${invoiceDeliveryMethod},,,(${invoice.name}) ${productSet?.displayName ?? item.productNames?.split(',').join('/')} (${
                            item.code
                        }: ${moment(item.invoiceDate).format('YYYY年MM月DD日')}検品),,${unitPrice},1,,`
                    csvList.push(row)
                    totalDiscount += item.discount ?? 0
                    total += totalExceptTax
                    row = ',,,'
                })
                totalTax = total * TAX_RATE
                total -= invoice.discount ?? 0
            })
            if (totalDiscount) {
                csvList.push(`,,,,,,,,,,,,,,,,,,,,,,,,,,,値引き,,${-totalDiscount},1,,`)
            }
            if (totalTax) {
                csvList.push(`,,,,,,,,,,,,,,,,,,,,,,,,,,,消費税,,${totalTax},1,,N00`)
            }
            if (total) {
                const splitFirst = csvList[firstIndex].split(',')
                splitFirst[splitFirst.length - 7] = String(total + totalTax)
                csvList[firstIndex] = splitFirst.join(',')
            }
        })
        api.response.invoices.forEach((invoice) => {
            const invoiceItems = invoice.items?.filter((item) => item.price != 0)
            if (invoiceItems?.length === 0) {
                return
            }
            const vm = new InstitutionViewModel(invoice)
            const invoiceDeliveryMethod = invoice.invoiceDeliveryMethod === 'POSTAL' ? '1' : '2'
            const invoiceSendEmail = invoice.invoiceSendEmail ? invoice.invoiceSendEmail : ''
            const invoiceSendEmailCc = invoice.invoiceSendEmailCc ? invoice.invoiceSendEmailCc : ''
            const invoiceSendDepartment = invoice.invoiceSendDepartment ? invoice.invoiceSendDepartment : ''
            const invoiceSendContactPerson = invoice.invoiceSendContactPerson ? invoice.invoiceSendContactPerson : ''

            let row = `${form.object.year}${String(form.object.month).padStart(2, '0')}_${invoice.code},${moment()
                .set('year', Number(form.object.year))
                .set('M', Number(form.object.month) - 1)
                .endOf('M')
                .format('YYYY/MM/DD')},${invoice.name},${invoiceSendDepartment},${invoiceSendContactPerson},${invoice.code},${vm.addressVm().zipCode()},${vm
                .addressVm()
                .whole()},${invoice.tel},,${invoiceSendEmail},${invoiceSendEmailCc},,,,,,,,,,,2,,${invoiceDeliveryMethod},,`
            let totalDiscount = invoice.discount ?? 0
            let total = 0
            const firstIndex = csvList.length
            invoiceItems?.forEach((item, index) => {
                const totalExceptTax = item.price! - item.discount!
                const unitPrice = item.price!
                const productSet = api.response.productSets?.find((ps) => ps.id === item.productSetId)
                row =
                    row +
                    `,${productSet?.displayName ?? item.productNames?.split(',').join('/')} (${item.code}: ${moment(item.invoiceDate).format(
                        'YYYY年MM月DD日'
                    )}検品),,${unitPrice},1,,`
                csvList.push(row)
                totalDiscount += item.discount ?? 0
                total += totalExceptTax
                row = ',,,,,,,,,,,,,,,,,,,,,,,,,,'
            })
            total -= invoice.discount ?? 0
            const TAX_RATE = 0.1
            const totalTax = total * TAX_RATE
            if (totalDiscount) {
                csvList.push(`,,,,,,,,,,,,,,,,,,,,,,,,,,,値引き,,${-totalDiscount},1,,`)
            }
            csvList.push(`,,,,,,,,,,,,,,,,,,,,,,,,,,,消費税,,${totalTax},1,,N00`)

            const splitFirst = csvList[firstIndex].split(',')
            splitFirst[splitFirst.length - 7] = String(total + totalTax)
            csvList[firstIndex] = splitFirst.join(',')
        })
        return csvList
    }

    useEffect(() => {
        if (!api.isSuccess) return
        const organizations =
            api.response.organizationInvoices?.filter(
                (organization) => (organization.invoices?.filter((invoice) => invoice?.items?.length ?? 0 > 0).length ?? 0) > 0
            ) ?? []
        const invoices = api.response.invoices.filter((invoice) => invoice.items?.length ?? 0 > 0) ?? []
        const organizationTotal = organizations.reduce(
            (sum, organization) =>
                sum +
                (organization.invoices?.reduce(
                    (sum, invoice) => sum + (invoice.items?.reduce((sum, item) => sum + (item.price! - item.discount!), 0) ?? 0),
                    0
                ) ?? 0),
            0
        )
        const invoiceTotal = invoices.reduce((sum, invoice) => sum + (invoice.items?.reduce((sum, item) => sum + (item.price! - item.discount!), 0) ?? 0), 0)
        const organizationSampleCount = organizations.reduce(
            (sum, organization) => sum + (organization.invoices?.reduce((sum2, invoice) => sum2 + (invoice.items?.length ?? 0), 0) ?? 0),
            0
        )
        const invoiceSampleCount = invoices.reduce((sum, invoice) => sum + (invoice.items?.length ?? 0), 0)

        const organizationsDiscount = organizations.reduce(
            (sum, organization) => sum + (organization.invoices?.filter((invoice) => invoice.discount ?? 0 > 0).length ?? 0),
            0
        )
        const invoiceDiscount = invoices.filter((invoice) => invoice.discount ?? 0 > 0).length

        setAggregate({
            organizationInstitutionCount: organizations.length + invoices.length,
            totalBilling: (organizationTotal + invoiceTotal).toLocaleString(),
            totalBillingWithTax: ((organizationTotal + invoiceTotal) * 1.1).toLocaleString(),
            sampleCount: organizationSampleCount + invoiceSampleCount,
            discountNumber: organizationsDiscount + invoiceDiscount,
        })
    }, [api.response])

    return (
        <>
            <ContentHeader
                headerTitle="請求"
                left={
                    <Button
                        variant="secondary"
                        onClick={() => {
                            const csvList = createInvoiceCsvList(true)
                            downloadFile(csvList.join('\n'), `misignal_${form.object.year}年${form.object.month}月_請求.csv`, 'text/csv', true)
                        }}
                    >
                        CSV出力
                    </Button>
                }
                right={
                    <>
                        <SelectField form={form} attr="year" selectItems={selectYears()} style={{ width: 120 }} />
                        <SelectField
                            form={form}
                            attr="month"
                            selectItems={new Array(12).fill(0).map((y, idx) => {
                                const month = idx + 1
                                return { label: month + '月', value: month }
                            })}
                        />
                    </>
                }
            />
            <div style={{ padding: 20, display: 'flex', justifyContent: 'flex-end', fontSize: 20, gap: 20 }}>
                {aggregate && (
                    <>
                        <div>{`病院・クリニック数: ${aggregate?.organizationInstitutionCount}`}</div>
                        <div>{`請求金額合計(税抜): ${aggregate?.totalBilling}`}</div>
                        <div>{`請求金額合計(税込): ${aggregate?.totalBillingWithTax}`}</div>
                        <div>{`商品数: ${aggregate?.sampleCount}`}</div>
                        <div>{`割引数: ${aggregate?.discountNumber}`}</div>
                    </>
                )}
            </div>
            <div style={{ padding: 20, marginTop: 5 }} className="shadow">
                <Table bordered>
                    <thead>
                        <tr>
                            <th style={{ width: 400 }}>病院・クリニック</th>
                            <th style={{ width: 100, textAlign: 'right' }}>取引金額</th>
                            <th style={{ minWidth: 500 }}>商品</th>
                            <th style={{ width: 80, textAlign: 'right' }}>単価</th>
                            <th style={{ width: 60, textAlign: 'right' }}>数量</th>
                            <th style={{ width: 0, textAlign: 'right' }}>金額</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const csvList = createInvoiceCsvList(false)
                            const elements = csvList.map((csv, i) => {
                                const list = csv.split(',')
                                let numChild = 0
                                if (list[2]) {
                                    let loop = true
                                    while (loop) {
                                        const nextRow = csvList[i + numChild + 1]
                                        if (!nextRow || nextRow.split(',')[2]) {
                                            loop = false
                                            break
                                        }
                                        numChild += 1
                                    }
                                }
                                return (
                                    <tr key={'invoice-' + i}>
                                        {list[2] && (
                                            <>
                                                <td rowSpan={numChild + 1}>{list[2]}</td>
                                                <td rowSpan={numChild + 1} align="right">
                                                    {list[list.length - 7] ? parseInt(list[list.length - 7])?.toLocaleString() : ''}
                                                </td>
                                            </>
                                        )}
                                        <td>{list[list.length - 6]}</td>
                                        <td align="right">{parseInt(list[list.length - 4]).toLocaleString()}</td>
                                        <td align="right">{parseInt(list[list.length - 3]).toLocaleString()}</td>
                                        <td align="right">{(parseInt(list[list.length - 4]) * parseInt(list[list.length - 3])).toLocaleString()}</td>
                                    </tr>
                                )
                            })
                            if (elements.filter((e) => !!e).length === 0) {
                                return (
                                    <tr>
                                        <td colSpan={1000} style={{ textAlign: 'center', padding: 20 }}>
                                            {form.object.year}年{form.object.month}月の請求はありません
                                        </td>
                                    </tr>
                                )
                            } else {
                                return elements
                            }
                        })()}
                    </tbody>
                </Table>
            </div>
        </>
    )
}

export default withRouter(InvoiePage)
